/*
 * Journey step: review and purchase.
 *
 * Collects every prior step into one cart add. Nothing is written until the
 * customer presses the button, so re-entering the step does not duplicate the
 * cart.
 *
 * Each item goes into the cart as its own normal line, so the customer can
 * change quantities, add or remove pieces individually. We deliberately do NOT
 * use the `_bundle_` line item property here: that key triggers the theme's
 * bundle-grouping, which collapses the whole build into a single uneditable
 * "Build a Kit" line.
 *
 * For reporting, every line carries one hidden tracking property
 * (`_kit_source`, value from the section, default "DIY Bundle"). It is a line
 * item property, not an order-level custom attribute, because order attributes
 * broke the Cin7 and 3PL sync earlier this year. The leading underscore hides
 * it from the customer while keeping it on the order line for reporting, and a
 * Shopify Flow can promote it to a real order tag on order creation.
 */
(function () {
  'use strict';

  function Review(root) {
    this.root = root;
    this.config = this.readConfig();
    if (!this.config) return;

    this.stepId = this.config.stepId || 'review';
    this.steps = this.config.steps || {};
    this.labels = this.config.labels || {};
    this.unit = this.config.units === 'm' ? 'm' : 'ft';

    this.groupsEl = root.querySelector('[data-rev-groups]');
    this.emptyEl = root.querySelector('[data-rev-empty]');
    this.summaryEl = root.querySelector('[data-rev-summary]');
    this.totalEl = root.querySelector('[data-rev-total]');
    this.buttonEl = root.querySelector('[data-rev-checkout]');
    this.buttonLabelEl = root.querySelector('[data-rev-checkout-label]');
    this.errorEl = root.querySelector('[data-rev-error]');
    this.heldBackEl = root.querySelector('[data-rev-held-back]');

    this.journey = null;
    this.submitting = false;

    this.money = this.moneyFormatter();
    this.bind();
  }

  Review.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-rev-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  Review.prototype.moneyFormatter = function () {
    var currency = this.config.currency || 'USD';
    try {
      // Pinned to the store's locale, not the visitor's. Left as undefined,
      // a visitor browsing from outside the US sees "USD 199.50" where the
      // rest of the storefront says "$199.50", because Intl falls back to a
      // currency code when the locale and currency disagree.
      var fmt = new Intl.NumberFormat(this.config.locale || 'en-US', { style: 'currency', currency: currency });
      return function (cents) {
        return fmt.format(cents / 100);
      };
    } catch (err) {
      return function (cents) {
        return '$' + (cents / 100).toFixed(2);
      };
    }
  };

  Review.prototype.bind = function () {
    var self = this;

    if (window.CatnetsJourney) {
      this.attach(window.CatnetsJourney);
    } else {
      document.addEventListener('journey:ready', function (e) {
        self.attach(e.detail.journey);
      });
    }

    document.addEventListener('journey:step:enter', function (e) {
      if (e.detail.step === self.stepId) self.render();
    });

    // Any earlier change should refresh the review while it is open.
    document.addEventListener('journey:step:complete', function () {
      self.render();
    });

    if (this.buttonEl) {
      this.buttonEl.addEventListener('click', function () {
        self.checkout();
      });
    }
  };

  Review.prototype.attach = function (journey) {
    this.journey = journey;
    this.render();
  };

  Review.prototype.data = function (key) {
    var id = this.steps[key] || key;
    return this.journey ? this.journey.data(id) : null;
  };

  /**
   * The complete build, as display groups. Also the source of the cart lines,
   * so what the customer sees is exactly what gets added.
   */
  Review.prototype.build = function () {
    var groups = [];
    var lines = [];

    // Titles of anything shown in the build but deliberately kept out of the
    // cart because the storefront cannot sell it right now.
    var unavailable = [];

    // Ends wider than the main run get their own net, so the netting step can
    // hand over several nets. Older saved journeys carry a single net at the
    // top level instead of a nets array.
    var netting = this.data('netting');
    var nets = [];
    if (netting) {
      if (netting.nets && netting.nets.length) nets = netting.nets;
      else if (netting.variantId) nets = [netting];
    }

    if (nets.length) {
      var rows = [];

      for (var n = 0; n < nets.length; n++) {
        var net = nets[n];
        var colourLabel = net.colourLabel || netting.colourLabel;
        var title = net.productTitle + ' - ' + net.variantTitle +
          (colourLabel ? ' (' + colourLabel + ')' : '');

        // With more than one net, say which part of the build each is for.
        if (nets.length > 1 && net.groupLabel) title += ' - ' + net.groupLabel;

        rows.push({
          title: title,
          quantity: net.quantity,
          total: net.totalPrice,

          // By the foot, "Qty 33" hides the size that matters. Show the cut:
          // feet of run by the width, e.g. "33ft x 10ft". Rolls carry their
          // size in the variant title already.
          qtyText: net.format === 'btm'
            ? net.quantity + this.unit + ' x ' + net.variantTitle
            : null
        });
        lines.push({ id: net.variantId, quantity: net.quantity });
      }

      groups.push({
        key: 'netting',
        label: this.labels.netting || 'Your netting',
        rows: rows
      });

      if (netting.zip && netting.zip.variantId) {
        var zipQty = netting.zip.quantity || 1;
        groups.push({
          key: 'zip',
          label: this.labels.zip || 'Your zip',
          rows: [{ title: netting.zip.title, quantity: zipQty, total: netting.zip.price * zipQty }]
        });
        lines.push({ id: netting.zip.variantId, quantity: zipQty });
      }
    }

    var hardware = this.data('hardware');
    if (hardware && hardware.items && hardware.items.length) {
      /*
       * A sold-out fixing is shown but never sent to the cart.
       *
       * Shopify's /cart/add.js rejects the ENTIRE payload if any one line is
       * unavailable, so a single out-of-stock item used to fail the whole
       * build with a generic error. Edging rope going out of stock broke all
       * 33 scenarios that way.
       *
       * It stays on screen, named, because a missing fixing means the build
       * will not work. Dropping it silently would hand someone an incomplete
       * kit and let them find out on the day.
       */
      groups.push({
        key: 'hardware',
        label: this.labels.hardware || 'Your hardware',
        rows: hardware.items.map(function (i) {
          return {
            title: i.title,
            quantity: i.quantity,
            total: i.total,
            unavailable: i.available === false
          };
        })
      });
      for (var h = 0; h < hardware.items.length; h++) {
        var hwItem = hardware.items[h];
        if (hwItem.available === false) {
          unavailable.push(hwItem.title);
          continue;
        }
        lines.push({ id: hwItem.variantId, quantity: hwItem.quantity });
      }
    }

    var skywalks = this.data('skywalks');
    if (skywalks && skywalks.items && skywalks.items.length) {
      groups.push({
        key: 'skywalks',
        label: this.labels.skywalks || 'Your Skywalks',
        rows: skywalks.items.map(function (i) {
          return { title: i.title, quantity: i.quantity, total: i.total };
        })
      });
      for (var sk = 0; sk < skywalks.items.length; sk++) {
        lines.push({ id: skywalks.items[sk].variantId, quantity: skywalks.items[sk].quantity });
      }
    }

    // The subtotal is what will actually be charged, so anything held back
    // from the cart is held back from the total too.
    var total = 0;
    for (var g = 0; g < groups.length; g++) {
      for (var r = 0; r < groups[g].rows.length; r++) {
        if (groups[g].rows[r].unavailable) continue;
        total += groups[g].rows[r].total;
      }
    }

    return { groups: groups, lines: lines, total: total, unavailable: unavailable };
  };

  Review.prototype.render = function () {
    if (!this.journey || !this.groupsEl) return;

    var build = this.build();

    if (!build.lines.length) {
      this.groupsEl.innerHTML = '';
      if (this.emptyEl) this.emptyEl.hidden = false;
      if (this.summaryEl) this.summaryEl.hidden = true;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.summaryEl) this.summaryEl.hidden = false;

    var qtyLabel = this.labels.qty || 'Qty';
    var html = '';

    for (var i = 0; i < build.groups.length; i++) {
      var group = build.groups[i];
      html += '<div class="jrev__group"><h4 class="jrev__group-title">' + group.label + '</h4>';

      for (var r = 0; r < group.rows.length; r++) {
        var row = group.rows[r];
        html +=
          '<div class="jrev__row' + (row.unavailable ? ' is-unavailable' : '') + '">' +
            '<span class="jrev__row-title">' + row.title + '</span>' +
            (row.unavailable
              ? '<span class="jrev__row-qty">' + (this.labels.soldOut || 'Out of stock') + '</span>' +
                '<span class="jrev__row-total">&mdash;</span>'
              : '<span class="jrev__row-qty">' + (row.qtyText || qtyLabel + ' ' + row.quantity) + '</span>' +
                '<span class="jrev__row-total">' + this.money(row.total) + '</span>') +
          '</div>';
      }

      html += '</div>';
    }

    this.groupsEl.innerHTML = html;
    this.renderHeldBack(build);
    if (this.totalEl) this.totalEl.textContent = this.money(build.total);
  };

  /**
   * Name anything kept out of the cart because it is out of stock.
   *
   * Deliberately specific. "Some items are unavailable" would leave someone
   * to work out which fixing is missing from a list of twenty, and hardware is
   * the one place where a missing piece means the build does not go together.
   */
  Review.prototype.renderHeldBack = function (build) {
    if (!this.heldBackEl) return;

    var names = build.unavailable || [];
    if (!names.length) {
      this.heldBackEl.hidden = true;
      this.heldBackEl.textContent = '';
      return;
    }

    var intro = this.labels.heldBackIntro || 'Out of stock, so not added to your cart:';
    this.heldBackEl.textContent = intro + ' ' + names.join(', ');
    this.heldBackEl.hidden = false;
  };

  Review.prototype.setError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  };

  Review.prototype.checkout = function () {
    if (this.submitting || !this.journey) return;

    var build = this.build();
    if (!build.lines.length) return;

    this.submitting = true;
    this.setError('');
    if (this.buttonEl) this.buttonEl.disabled = true;
    if (this.buttonLabelEl) this.buttonLabelEl.textContent = this.labels.adding || 'Adding...';

    // One hidden tracking tag per line, so orders placed through the journey are
    // reportable without collapsing the lines into a single bundle.
    var tag = this.config.bundleTag || 'DIY Bundle';

    var payload = {
      items: build.lines.map(function (line) {
        return {
          id: line.id,
          quantity: line.quantity,
          properties: { _kit_source: tag }
        };
      })
    };

    var self = this;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (response.ok) return response.json();

        /*
         * Shopify says exactly what went wrong, so pass it on.
         *
         * A 422 carries a description like "The product 'Edging Rope: 164ft
         * Bulk Roll - Black' is already sold out." Swallowing that in favour
         * of "Something went wrong" is what turned a sold-out rope into an
         * unexplained failure that needed a live reproduction to diagnose.
         */
        return response.json().then(
          function (body) {
            var reason = body && (body.description || body.message);
            throw new Error(reason || 'cart add failed: ' + response.status);
          },
          function () {
            throw new Error('cart add failed: ' + response.status);
          }
        );
      })
      .then(function () {
        // Announce the successful add for analytics before leaving the page.
        try {
          document.dispatchEvent(new CustomEvent('journey:cart:added', {
            detail: { value: build.total, itemCount: build.lines.length },
            bubbles: true
          }));
        } catch (err) { /* tracking must never block checkout */ }

        var url = self.config.checkoutUrl || '/cart';
        if (self.config.campaign) {
          url += (url.indexOf('?') > -1 ? '&' : '?') + 'utm_campaign=' + encodeURIComponent(self.config.campaign);
        }
        self.go(url);
      })
      .catch(function (err) {
        self.submitting = false;
        if (self.buttonEl) self.buttonEl.disabled = false;
        if (self.buttonLabelEl) self.buttonLabelEl.textContent = self.labels.checkout || 'Add everything to cart';
        // Prefer Shopify's own words when it gave us any.
        var reason = err && err.message && err.message.indexOf('cart add failed') === -1
          ? err.message
          : (self.labels.error || 'Something went wrong. Please try again.');
        self.setError(reason);
      });
  };

  // Single seam for the post-add redirect, so it can be observed in tests where
  // a real navigation is not possible.
  Review.prototype.go = function (url) {
    window.location.href = url;
  };

  function boot() {
    var roots = document.querySelectorAll('[data-journey-step-source]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('[data-rev-config]')) {
        window.CatnetsReview = new Review(roots[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
